// src/components/dashboard/wall/PreviousChallangeLeaderBoardTableComponent.jsx
import { useEffect, useMemo, useRef, useState, memo } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT } from "../../../../../lib/api-url";

const DEFAULT_AVATAR = "/avatar/avatar-default.webp";
const COL_SIZE = 5; // 5 items per column
const BATCH_SIZE = 10; // 5 + 5
const REST_MS = 8000; // 5 seconds rest between pages / on data change
const ENTER_STAGGER = 0.18; // seconds per item
const EXIT_STAGGER = 0.12; // seconds per item

function safeNum(n, fallback = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function shapeRow(it) {
  const rank =
    safeNum(it?.stage_3_final_global_rank_in_level) || safeNum(it?.rank) || 0;

  const points = safeNum(it?.points, 0);
  const equity = safeNum(it?.stage_3_score, 0);
  const balance = safeNum(it?.balance, 0);

  const loginNum = safeNum(it?.account_login);
  const name =
    [it?.first_name, it?.last_name].filter(Boolean).join(" ").trim() ||
    String(it?.username ?? "");
  console.log("before map ", it)
  return {
    id: loginNum || safeNum(it?.user_id) || name,
    login: loginNum,
    name,
    equity,
    balance,
    points,
    rank,
    avatar:it?.avatar,
    profileImage: DEFAULT_AVATAR,
  };
}

const itemVariantsSmooth = {
  hidden: { opacity: 0, x: 56, filter: "blur(4px)" },
  // we’ll use "custom" to compute delays so both columns follow a single global order: 1..10
  show: (i) => ({
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: {
      type: "tween",
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      delay: i * ENTER_STAGGER,
    },
  }),
  // reverse-stagger exit: last leaves first (10 -> 1)
  exit: (i) => ({
    opacity: 0,
    x: -56,
    filter: "blur(4px)",
    transition: {
      type: "tween",
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
      delay: (BATCH_SIZE - 1 - i) * EXIT_STAGGER,
    },
  }),
};

function PreviousChallangeLeaderBoardTableComponent({
  onDataReady,
  play = true,
}) {
  const [items, setItems] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const notifiedRef = useRef(false);
  const reduceMotion = useReducedMotion();
  const timerRef = useRef(null);

  const itemVariants = reduceMotion
    ? {
        hidden: { opacity: 0 },
        show: (i) => ({
          opacity: 1,
          transition: { duration: 0.2, delay: i * 0.05 },
        }),
        exit: (i) => ({
          opacity: 0,
          transition: { duration: 0.15, delay: (BATCH_SIZE - 1 - i) * 0.04 },
        }),
      }
    : itemVariantsSmooth;

  const notifyReadyOnce = () => {
    if (!notifiedRef.current) {
      notifiedRef.current = true;
      onDataReady?.();
    }
  };

  // fetch once
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(API_ENDPOINT.users.getFinalistsData, {
          signal: controller.signal,
        });
        const arr = res?.data?.items ?? [];
        setItems(Array.isArray(arr) ? arr : []);
        notifyReadyOnce();
      } catch (e) {
        if (e.name !== "CanceledError" && e.message !== "canceled") {
          console.error("Leaderboard API error:", e);
          notifyReadyOnce();
        }
      }
    })();
    return () => controller.abort();
  }, []);

  // shape & sort by rank ascending (undefined ranks go to the end)
  const rowsByRank = useMemo(() => {
    const shaped = (Array.isArray(items) ? items : []).map(shapeRow);
    shaped.sort((a, b) => {
      const ar = a.rank || Number.MAX_SAFE_INTEGER;
      const br = b.rank || Number.MAX_SAFE_INTEGER;
      return ar - br;
    });
    return shaped;
  }, [items]);

  // Build batches of 10 by rank order (1..10, then 11..20, etc.)
  const batches = useMemo(() => {
    if (!rowsByRank.length) return [];
    const arr = [...rowsByRank];
    const tmp = [];
    for (let i = 0; i < arr.length; i += BATCH_SIZE) {
      tmp.push(arr.slice(i, i + BATCH_SIZE));
    }
    return tmp;
  }, [rowsByRank]);

  const totalPages = batches.length || 1;

  // When data changes (or number of pages changes), reset to page 0
  useEffect(() => {
    setPageIndex(0);
  }, [totalPages, rowsByRank]);

  // Auto-advance pages with 5s rest
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setPageIndex((p) => (p + 1) % Math.max(totalPages, 1));
    }, REST_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [pageIndex, totalPages]);

  // Derive current batch (10 max)
  const currentBatch = batches[pageIndex] ?? [];

  // Map into two columns using rank-interleaving
  const withOrderIndex = useMemo(
    () => currentBatch.map((user, i) => ({ ...user, __orderIndex: i })),
    [currentBatch]
  );

  const firstCol = useMemo(
    () =>
      withOrderIndex
        .filter((u) => safeNum(u.rank, Infinity) % 2 === 1)
        .slice(0, COL_SIZE),
    [withOrderIndex]
  );
  const secondCol = useMemo(
    () =>
      withOrderIndex
        .filter((u) => safeNum(u.rank, Infinity) % 2 === 0)
        .slice(0, COL_SIZE),
    [withOrderIndex]
  );

  // If there are missing ranks inside the batch, fall back to positional parity
  const fallbackIfEmpty = (col, parity) => {
    if (col.length) return col;
    const byPos = withOrderIndex
      .filter((_, i) => i % 2 === parity)
      .slice(0, COL_SIZE);
    return byPos;
  };

  const colA = fallbackIfEmpty(firstCol, 0); // first column
  const colB = fallbackIfEmpty(secondCol, 1); // second column

  const fmt = (v) =>
    v == null || Number.isNaN(Number(v))
      ? "-"
      : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const Header = () => (
    <div className="header-component w-[400px] relative">
      <img
        src="/red-table/red-table-header.svg"
        className="w-[240px] z-[2] absolute top-[-20px] left-[32px]"
        alt=""
      />
      <div className="z-[4] right-[45%] top-[-16px] w-[200px] header-text-container flex items-center justify-around absolute gap-[9px]">
        <span className="text-[10px] text-[#FFFFFF] font-semibold">امتیاز</span>
        <span className="text-[10px] text-[#FFFFFF] font-semibold">رتبه</span>
      </div>
    </div>
  );

  // Card styles untouched; we only add exit + custom index for stagger control
  const Card = ({ user, orderIndex }) => (
    <motion.div
      className="card-container relative w/[400px] z-[3]"
      variants={itemVariants}
      initial="hidden"
      animate={play ? "show" : "hidden"}
      exit="exit"
      custom={orderIndex}
      layout="position"
    >
      <img src="/red-table/red-table-banner.svg" className="w-[450px]" alt="" />
      <img
        src="/red-table/red-table-sub-banner.svg"
        className="absolute right-[64px] bottom-[-8px] w-[137px]"
        alt=""
      />
      <img
        src="/red-table/red-table-user-box.svg"
        className="absolute right-0 top-[-4px] w-[83px]"
        alt=""
      />
      <img
        src={ user.avatar ? `https://backtest.ahangariholding.com/${user.avatar}` : DEFAULT_AVATAR}
        className={user.avatar ?"w-[64px] absolute right-[12px] top-[-44px]":"w-[54px] absolute right-[16px] top-[-32px]"}
        alt={user.name}
        loading="lazy"
      />

      <div className="text-container flex items-center justify-center gap-3 absolute right-[90px] top-[10px]">
        <div className="points-container flex justify-around items-center gap-[24px]   w-[200px]">
          <span className="text-[#FFFFFF] text-[16px] font-bold">
            {fmt(user.points)}
          </span>

          <span className="text-[#FFFFFF] text-[16px] font-bold">
            {user.rank || "-"}
          </span>
        </div>
        <span className="text-[#FFFFFF] text-[10px] font-bold w-[80px] text-right text-nowrap ">
          {user.name}
        </span>
      </div>
    </motion.div>
  );

  const orderIndexOf = (u) => {
    const i = withOrderIndex.findIndex((x) => x.id === u.id);
    return i === -1 ? 0 : i;
  };
  console.log("col A",colA)
  return (
    <div
      style={{
        scale: "1.5",
      }}
      className="flex justify-center gap-[182px] mb-[32px] flex-row-reverse"
    >
      <div className="flex flex-col items-center">
        <Header />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`colA-page-${pageIndex}`}
            className="w-full flex flex-col items-center justify-center gap-[38px]"
            presenceAffectsLayout={false}
          >
            {colA.slice(0, COL_SIZE).map((user) => (
              <Card key={user.id} user={user} orderIndex={orderIndexOf(user)} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Column B */}
      <div className="flex flex-col items-center">
        <Header />
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`colB-page-${pageIndex}`}
            className="w-full flex flex-col items-center justify-center gap-[38px]"
            presenceAffectsLayout={false}
          >
            {colB.slice(0, COL_SIZE).map((user) => (
              <Card key={user.id} user={user} orderIndex={orderIndexOf(user)} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default memo(PreviousChallangeLeaderBoardTableComponent);
